// Direct-manipulation orders on the map (spec Addendum A). The deck stays on screen as the reference
// hand and the cost display; this is how orders are actually given.
//
//   left press on a friendly unit, move past the threshold, release
//       = DRAG TO MOVE. A preview axis follows the cursor with the card's three costs beside it, and the
//         drop point becomes `move → {kind:'point', cell}`. Esc throws the drag away.
//   left press and release without moving
//       = the selection click the overlay already handled; this controller stays out of it.
//   right press on a friendly unit
//       = RADIAL MENU. Drag out past the dead zone to light a wedge, release to play it. A card with no
//         target goes straight onto the command net; anything else hands over to the stage's targeting
//         mode, which already owns the crosshair, the preview, the banner and the commit click.
//   long press (touch)
//       = the radial menu, left up for a tap.
//
// The state machine is one variable:
//
//   idle --left press on unit--> press --moved > 6 px--> drag --release--> idle (order issued)
//        --right press on unit-> radial --release on a wedge--> idle (card played / targeting)
//                                       --release in the dead zone--> radial stays up for a click
//
// Nothing here talks to React. The radial menu is driven through a `RadialController`, and everything
// else goes through the store, so the only DOM this module owns is the cost tag and the refusal toast.

import type { Map as MapLibreMap } from 'maplibre-gl';
import type { Overlay } from '../render/Overlay';
import type { Projection } from './projection';
import type { Grid } from '../sim/grid';
import { card } from '../sim/deck';
import { useStore } from '../state/store';
import { fmtDur } from '../ui/cards/ui';
import { transmitEstimateSec } from '../ui/deck/delay';
import { RADIAL_ARM, radialFit } from '../ui/radial/layout';
import type { RadialController } from '../ui/radial/state';

/** How far a press has to travel before it is a drag rather than a click. */
const DRAG_PX = 6;
/** How close, in screen pixels, a press has to land to count as pressing a unit symbol. */
const UNIT_HIT_PX = 20;
/** A release inside the dead zone this soon after opening leaves the menu up for a click. */
const STICKY_MS = 400;
/** Touch: how long a finger has to rest on a unit before the radial menu opens. */
const LONG_PRESS_MS = 450;
/** How long a refused order's reason stays on screen. */
const TOAST_MS = 2600;
/** Rings searched outward when a drop lands on ground the unit cannot reach. */
const SNAP_RINGS = 24;

export interface GestureDeps {
  map: MapLibreMap;
  /** The element the Pixi overlay canvas lives in; `map.project` output is already in its space. */
  container: HTMLElement;
  projection: Projection;
  overlay: Overlay;
  radial: RadialController;
  /**
   * What the stage's rails and bars cover, in screen pixels. The radial menu is kept inside what is
   * left, so a unit sitting in its assembly area on the bottom edge - which is where every unit starts -
   * still gets a whole menu rather than the half of it that happened to fit.
   */
  insets?: { left: number; right: number; top: number; bottom: number };
}

type Mode = 'idle' | 'press' | 'drag' | 'radial';

/**
 * The nearest cell the unit could actually be ordered to, when the drop lands in water or on a slope
 * the flow field will not cross. Searched as square rings outward, nearest first within a ring.
 */
export function snapToPassable(grid: Grid, cell: number): number | null {
  if (grid.passable(cell)) return cell;
  const cx = grid.cx(cell);
  const cy = grid.cy(cell);
  for (let r = 1; r <= SNAP_RINGS; r++) {
    let best: { cell: number; d: number } | null = null;
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = cx + dx;
        const y = cy + dy;
        if (!grid.inBounds(x, y)) continue;
        const i = grid.index(x, y);
        if (!grid.passable(i)) continue;
        const d = dx * dx + dy * dy;
        if (!best || d < best.d) best = { cell: i, d };
      }
    }
    if (best) return best.cell;
  }
  return null;
}

export function attachMapGestures({
  map, container, projection, overlay, radial, insets = { left: 0, right: 0, top: 0, bottom: 0 },
}: GestureDeps): () => void {
  let mode: Mode = 'idle';
  let pressUnit: number | null = null;
  let pressAt = { x: 0, y: 0 };
  let pointerId = -1;
  let radialCenter = { x: 0, y: 0 };
  /** Where the pointer was when the menu opened: the arming distance is measured from here, never from
   * the hub, which may have been reflowed some distance away to fit between the rails. */
  let radialOrigin = { x: 0, y: 0 };
  let radialOpenedAt = 0;
  let radialTouch = false;
  let longPress: ReturnType<typeof setTimeout> | null = null;
  let toastTimer: ReturnType<typeof setTimeout> | null = null;

  // ---- the two pieces of DOM this controller owns ------------------------------------------------

  const tag = document.createElement('div');
  tag.className = 'mono';
  tag.setAttribute('data-testid', 'drag-cost-tag');
  Object.assign(tag.style, {
    position: 'absolute', left: '0', top: '0', zIndex: '6', display: 'none',
    pointerEvents: 'none', whiteSpace: 'nowrap',
    padding: '2px 6px', borderRadius: '2px',
    border: '1px solid var(--color-friendly)',
    background: 'color-mix(in srgb, var(--color-slate-900) 92%, transparent)',
    color: 'var(--color-paper)', fontSize: '10px', letterSpacing: '0.08em',
  });
  container.appendChild(tag);

  const toast = document.createElement('div');
  toast.className = 'mono fade-in';
  toast.setAttribute('data-testid', 'gesture-toast');
  Object.assign(toast.style, {
    position: 'absolute', left: '50%', top: '104px', transform: 'translateX(-50%)',
    zIndex: '25', display: 'none', pointerEvents: 'none', whiteSpace: 'nowrap',
    padding: '4px 10px', borderRadius: '2px',
    border: '1px solid var(--color-hostile)',
    background: 'color-mix(in srgb, var(--color-slate-900) 95%, transparent)',
    color: 'var(--color-hostile)', fontSize: '11px', letterSpacing: '0.08em',
  });
  container.appendChild(toast);

  const showToast = (text: string): void => {
    toast.textContent = text;
    toast.style.display = 'block';
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { toast.style.display = 'none'; }, TOAST_MS);
  };

  // ---- geometry ----------------------------------------------------------------------------------

  const local = (e: { clientX: number; clientY: number }): { x: number; y: number } => {
    const rect = container.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const cmAt = (x: number, y: number): { x: number; y: number } => {
    const ll = map.unproject([x, y]);
    return projection.lngLatToCm(ll.lng, ll.lat);
  };

  /** The friendly unit whose symbol is under a point, or null. Same rule as the overlay's own click. */
  const unitAt = (x: number, y: number): number | null => {
    const snap = useStore.getState().snapshot;
    if (!snap) return null;
    let best: { id: number; d: number } | null = null;
    for (const u of snap.units) {
      if (u.side !== 0 || !u.alive) continue;
      const p = overlay.screenPosOf(u.id);
      if (!p) continue;
      const d = Math.hypot(p.x - x, p.y - y);
      if (d <= UNIT_HIT_PX && (!best || d < best.d)) best = { id: u.id, d };
    }
    return best ? best.id : null;
  };

  // ---- drag to move ------------------------------------------------------------------------------

  const showCosts = (x: number, y: number, unitId: number): void => {
    const s = useStore.getState();
    const unit = s.snapshot?.units.find((u) => u.id === unitId) ?? null;
    const hqAlive = s.snapshot?.units.some((u) => u.side === 0 && u.type === 'hq' && u.alive) ?? true;
    const def = card('move');
    const tx = transmitEstimateSec(s.dials, unit, hqAlive);
    tag.textContent = `move · issue ${fmtDur(def.issueSec)} · tx ${fmtDur(tx)} · exec ${fmtDur(def.execSec)}`;
    tag.style.transform = `translate(${Math.round(x + 16)}px, ${Math.round(y + 16)}px)`;
    tag.style.display = 'block';
  };

  const drawDrag = (x: number, y: number, unitId: number): void => {
    // The line starts at the symbol the player is actually looking at, which under C2 (command and
    // control) is the unit's *reported* position rather than its true one (spec §7).
    const sp = overlay.screenPosOf(unitId);
    const unit = useStore.getState().snapshot?.units.find((u) => u.id === unitId);
    const from = sp ? cmAt(sp.x, sp.y) : unit ? { x: unit.x, y: unit.y } : null;
    if (!from) return;
    overlay.setPreview({ id: 'drag-move', kind: 'axis', name: '', pts: [from, cmAt(x, y)] });
    showCosts(x, y, unitId);
  };

  const clearDrag = (): void => {
    overlay.setPreview(null);
    tag.style.display = 'none';
  };

  const commitDrag = (x: number, y: number, unitId: number): void => {
    clearDrag();
    const s = useStore.getState();
    if (!s.grid) return;
    const cm = cmAt(x, y);
    const dropped = s.grid.cellOfCm(cm.x, cm.y);
    const cell = snapToPassable(s.grid, dropped);
    if (cell === null) {
      showToast('No ground there the unit could reach');
      return;
    }
    s.issue(unitId, 'move', { kind: 'point', cell });
    // `issue` reports a refusal by setting `targetingError` (it is the targeting banner's channel). A
    // dragged order has no banner, so the reason is taken here and shown as its own brief toast.
    const err = useStore.getState().targetingError;
    if (err) {
      showToast(err);
      useStore.setState({ targetingError: null });
    }
  };

  // ---- the radial menu ---------------------------------------------------------------------------

  const openRadial = (unitId: number, at: { x: number; y: number }, touch: boolean): void => {
    const anchor = overlay.screenPosOf(unitId) ?? at;
    const rect = container.getBoundingClientRect();
    // Reflow, not clamp: a unit in its assembly area on the bottom edge gets a smaller menu drawn where
    // it is, rather than a full-size one dragged a hundred pixels inboard (src/ui/radial/layout.ts).
    const { center, scale } = radialFit(anchor, { width: rect.width, height: rect.height }, insets);
    const s = useStore.getState();
    s.stopTargeting();
    s.select(unitId);
    if (!radial.open(unitId, center, anchor, scale)) return;
    radialCenter = center;
    radialOrigin = at;
    radialOpenedAt = performance.now();
    radialTouch = touch;
    mode = 'radial';
  };

  // ---- pointer plumbing --------------------------------------------------------------------------

  /** Pointer capture keeps the moves and the release coming here even when the cursor leaves the map. */
  const capture = (id: number): void => {
    try { container.setPointerCapture(id); } catch { /* a pointer that is already gone: nothing to hold */ }
  };

  const release = (): void => {
    try {
      if (pointerId >= 0 && container.hasPointerCapture(pointerId)) container.releasePointerCapture(pointerId);
    } catch { /* already released */ }
    pointerId = -1;
    pressUnit = null;
    mode = 'idle';
    map.dragPan.enable();
    if (longPress) { clearTimeout(longPress); longPress = null; }
  };

  const onPointerDown = (e: PointerEvent): void => {
    const { x, y } = local(e);
    // A press anywhere on the map closes a menu that is waiting for a click. The menu itself is a
    // sibling of this container, so its own wedges never reach here.
    if (radial.isOpen() && radial.isSticky()) radial.close();

    if (e.button === 2) {
      const id = unitAt(x, y);
      e.preventDefault();
      if (id === null) { radial.close(); return; }
      map.dragPan.disable();
      pointerId = e.pointerId;
      capture(e.pointerId);
      openRadial(id, { x, y }, false);
      return;
    }
    if (e.button !== 0) return;

    // An order already hunting for a target owns the next click (the stage's targeting mode).
    if (useStore.getState().targeting) return;

    const id = unitAt(x, y);
    if (id === null) return;
    mode = 'press';
    pressUnit = id;
    pressAt = { x, y };
    pointerId = e.pointerId;
    capture(e.pointerId);
    // A drag on a unit is an order, never a camera move.
    map.dragPan.disable();
    if (e.pointerType === 'touch') {
      longPress = setTimeout(() => {
        if (mode !== 'press' || pressUnit === null) return;
        openRadial(pressUnit, pressAt, true);
      }, LONG_PRESS_MS);
    }
  };

  const onPointerMove = (e: PointerEvent): void => {
    const { x, y } = local(e);
    if (mode === 'radial') {
      radial.track(x - radialCenter.x, y - radialCenter.y, Math.hypot(x - radialOrigin.x, y - radialOrigin.y));
      return;
    }
    if (mode === 'press' && pressUnit !== null) {
      if (Math.hypot(x - pressAt.x, y - pressAt.y) <= DRAG_PX) return;
      if (longPress) { clearTimeout(longPress); longPress = null; }
      mode = 'drag';
      useStore.getState().select(pressUnit);
    }
    if (mode === 'drag' && pressUnit !== null) drawDrag(x, y, pressUnit);
  };

  const onPointerUp = (e: PointerEvent): void => {
    const { x, y } = local(e);
    if (mode === 'radial') {
      // How far the pointer actually travelled since the menu opened - not how far it is from the hub.
      const moved = Math.hypot(x - radialOrigin.x, y - radialOrigin.y);
      const armed = radial.track(x - radialCenter.x, y - radialCenter.y, moved);
      if (armed && armed.legal) {
        radial.choose(armed.card);
      } else if (armed) {
        // A greyed wedge: leave the menu up, the wedge is already saying why.
        radial.setSticky(true);
      } else if (moved <= RADIAL_ARM && (radialTouch || performance.now() - radialOpenedAt < STICKY_MS)) {
        // Right-click-and-let-go, or a long press: the menu stays up and waits for a click. This is now
        // the *only* thing a release without movement can do - it used to be able to issue an order.
        radial.setSticky(true);
      } else {
        radial.close();
      }
    } else if (mode === 'drag' && pressUnit !== null) {
      commitDrag(x, y, pressUnit);
    }
    release();
  };

  const onPointerCancel = (): void => {
    if (mode === 'drag') clearDrag();
    if (mode === 'radial') radial.close();
    release();
  };

  const onContextMenu = (e: MouseEvent): void => {
    const { x, y } = local(e);
    if (radial.isOpen() || unitAt(x, y) !== null) e.preventDefault();
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    if (mode === 'drag') {
      clearDrag();
      release();
      return;
    }
    if (radial.isOpen()) {
      radial.close();
      release();
    }
  };

  container.addEventListener('pointerdown', onPointerDown);
  container.addEventListener('pointermove', onPointerMove);
  container.addEventListener('pointerup', onPointerUp);
  container.addEventListener('pointercancel', onPointerCancel);
  container.addEventListener('contextmenu', onContextMenu);
  window.addEventListener('keydown', onKeyDown);

  return () => {
    container.removeEventListener('pointerdown', onPointerDown);
    container.removeEventListener('pointermove', onPointerMove);
    container.removeEventListener('pointerup', onPointerUp);
    container.removeEventListener('pointercancel', onPointerCancel);
    container.removeEventListener('contextmenu', onContextMenu);
    window.removeEventListener('keydown', onKeyDown);
    if (toastTimer) clearTimeout(toastTimer);
    if (longPress) clearTimeout(longPress);
    radial.close();
    map.dragPan.enable();
    tag.remove();
    toast.remove();
  };
}
